import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Socket } from 'ngx-socket-io';
import { PGUtilities } from '../utilities/pg-utilities';
import { PgFileUtils } from '../models/file.model';
import { PGLocation } from '../models/map.model';

export type EnvironmentDataAppMode = 'web'|'kiosk'|'hybrid';

export class EnvironmentData {
    /** Entourance public API (e.g. https://api.dev.entourance.com/api) */
    EntouranceAPIUrl: string = null;
    /** Tourist travel API used for login/OTP/travels (e.g. https://dev.travel.entourance.com/api) */
    TravelAPIUrl: string = null;
    AppName: string = null;
    AppLogo: string = null;
    AppProvider: string = null;

    AppMode?:EnvironmentDataAppMode = null;

    GoogleMapsAPIKey: string = null;
    StripeAPIKey?:string = null;

    AvailableApplicationLanguages?:Array<string> = null;
    DefaultContentLanguages?:Array<string> = null;
    AvailableContentCurrencies?:Array<string> = null;

    WebSocket: string = null;
    
    EntouranceURL: string = null;
    WebTourURL: string = null;
    CheckoutURL: string = null;
    FormsURL: string = null;
    ExperienceFinderURL: string = null;
    TotemURL: string = null;
    CustomerCareURL: string = null;
    ViewerURL: string = null;

    PrivacyPolicyURL?:string = null;
    TermsAndConditionsURL?:string = null;

    FileSizeLimit?:number|string = null;
    DisableSMS?:boolean = null;

    CustomOptionMaps?:{ [resource:string]: { [field:string]: Array<string | { value: string, data:any }> } } = null;

    ExperienceFinderResources?:Array<string> = null;
    ExperienceFinderGeoJSON?: { [category:string]: string } = null;
    ExperienceFinderSort?:'distance'|'alphabetical'|'shuffle' = null;
    ExperienceFinderShowcaseHideQROnFlyer?:boolean = null;
    ExperienceFinderShowcaseTag?:string = null;
    ExperienceSatelliteMap?:boolean = null;

    ConnectorsEnabled?:{ [connector:string]: boolean } = null;

    InstallationName?:string = null;
    CustomerCareTimeout?:number = null;
    LoginCodePath?:string = null;
}

export type ConnectorType = 'TDH'|'DHM';

export class ApplicationStatus {
    maintenance?:{ from?: string, to?: string }
}

export class ExternalParametersData {
    returnUrl?:string;
    language?:string;
    
    iframe?:string;
    iframekiosk?:string;

    forceEnv?:string;
    
    [x: string]: string;
}

export class RealmDefaultData {
    images: {
        [resource:string]: {
            image: string,
            specific: Array<{ 
                field: string,
                value: string,
                image: string
            }>
        }
    }
}

export class RealmContactData {
    name?:string
    description?:string
    email?:string
    phone?:string
    website?:string
}


export class RealmConfigurationData {
    hideRequestAccess?:boolean
    denyPendingAccess?:boolean
    importWordPress?:string
    importAPI?: Array<{ name?: string, url?: string }>;
    visitCardTotem?:string;
    cumulativeBookings?:boolean;
    tagImages?: Array<RealmTagImageData>;
}

export class RealmTagImageData { 
    tag: string
    image: string 
}

export class RealmData {
    id:string = null;
    name:string = null;
    logo:string = null;
    location: PGLocation = null;
    city:string = null;
    website:string = null;
    contacts: Array<RealmContactData> = []
    default: RealmDefaultData = null;
    configuration: RealmConfigurationData = {};
    active_apps: { [id:string]: boolean } = {};
    additional_languages:Array<string> = [];

    constructor(fromData?:any) {
        if(fromData != null) {
            let _JSONProps = ['contacts', 'location', 'default', 'configuration', 'active_apps', 'additional_languages']

            for(let i in fromData) {
                if(typeof this[i] != 'undefined') {
                    if(_JSONProps.includes(i)) {
                        let _json = PGUtilities.tryParseJSON(fromData[i])
                        if(_json != null) this[i] = _json;
                    }
                    else this[i] = fromData[i]
                }
            }

            if(fromData.active_apps == null) {
                if(this.name == 'null') this.active_apps = { 'SHAPEUP/dms': true, 'connector/TDH': true, 'connector/DHM': true };
                else this.active_apps = {};
            }
        }
    }
}

export class VersionData {
    major:number
    minor:number
    patch:number
    build?:number
    prefix?:string
    suffix?:string
    time?:string
}

@Injectable({
    providedIn: 'root'
})
export class EnvironmentService {
    constructor(private http:HttpClient) {}

    production:boolean = null;

    applicationName:string = null;

    frontendVersion:VersionData = null;
    apiVersion:string = null;
    websocketVersionLocal:VersionData = null;
    websocketVersionGlobal:VersionData = null;

    frontendVersionChange = new Subject<VersionData>();

    environment:EnvironmentData = new EnvironmentData();

    applicationStatus:ApplicationStatus = {};
    applicationStatusChange = new Subject<ApplicationStatus>();

    externalParameters:ExternalParametersData = {}

    fileSizeLimitNumber:number = Infinity;
    fileSizeLimitString:string = '';

    ElectronWrapper:{
        getAppEnvironment:() => Promise<any>,
        printContents:() => Promise<void>
    } = null;

    async init(env:any, application:string, ignoreWebSocket?:boolean) {
        this.applicationName = application;

        this.production = env.production;

        await this._watchApplicationVersion();

        // check stato maintenance

        await this._watchApplicationStatus(env);

        if(this.getTimeUntilManteinance() > 0) {

            // carico eventuali parametri esterni

            let _queryParamsSplit:Array<string> = [];
            if(window.location.search != null) _queryParamsSplit = window.location.search.replace(/^\?/, '').split('&');
    
            for(let _cSplit of _queryParamsSplit) {
                let _cSubSplit = _cSplit.split('=');
                this.externalParameters[_cSubSplit[0]] = decodeURIComponent(_cSubSplit[1])
            }
    
            let _envData:any = {}

            // EnvironmentURL da environment.ts (es. assets/environment.json)
            if(env.EnvironmentURL == null) {
                _envData = env;
            }
            else {
                _envData = await new Promise<any>((resolve) => {
                    this.http.get(env.EnvironmentURL + '?_=' + Date.now()).subscribe((data) => {
                        resolve(data);
                    }, () => {
                        resolve(null);
                    })
                })

                if(_envData == null) _envData = {};
            }

            // se non c'è un EntouranceAPIUrl specificato, uso quello di default
            if(_envData.EntouranceAPIUrl == null) {
                console.log('Using default EntouranceAPIUrl')
                _envData.EntouranceAPIUrl = window.location.protocol + '//api.' + window.location.host + '/api'
                console.log(_envData.EntouranceAPIUrl)
            }

            this.apiVersion = null;

            // Environment solo da file (assets/environment.json) — niente GET /environment su Entourance
            for(let i in _envData) {
                if(typeof this.environment[i] == 'undefined') {
                    console.log('Found unused environment setting: ' + i, _envData[i])
                }
                else {
                    this.environment[i] = _envData[i];
                }
            }
    
            if(this.environment.AppMode == null) {
                this.environment.AppMode = 'web';
            }

            if(this.environment.AvailableContentCurrencies == null) {
                this.environment.AvailableContentCurrencies = ['EUR']
            }
            
            if(this.environment.FileSizeLimit != null) {
                if(typeof this.environment.FileSizeLimit == 'number') {
                    this.fileSizeLimitNumber = this.environment.FileSizeLimit;
                }
                else if(typeof this.environment.FileSizeLimit == 'string') {
                    this.fileSizeLimitNumber = PgFileUtils.getFileSizeNumber(this.environment.FileSizeLimit)
                }
    
                this.fileSizeLimitString =  PgFileUtils.getFileSizeString(this.fileSizeLimitNumber)
    
                console.log('FileSizeLimit', this.fileSizeLimitString, this.fileSizeLimitNumber)
            }

            // controllo se sono nell'ElectronWrapper

            this.ElectronWrapper = window['ElectronWrapper'];

            if(this.ElectronWrapper != null) {
                console.log('Found ElectronWrapper')

                let _electronAppEnvironment = await this.ElectronWrapper.getAppEnvironment()

                if(_electronAppEnvironment != null) {
                    console.log('Using ElectronWrapper AppEnvironment', _electronAppEnvironment)

                    for(let i in _electronAppEnvironment) {
                        this.environment[i] = _electronAppEnvironment[i];
                    }
                }
            }
            else {
                if(this.externalParameters.forceElectron != null) {
                    console.log('Forcing ElectronWrapper')
                    
                    this.ElectronWrapper = {
                        getAppEnvironment: () => {
                            return new Promise((resolve) => {
                                resolve({})
                            })
                        },
                        printContents: () => {
                            return new Promise((resolve) => {
                                resolve()
                            })
                        },
                    }
                }
            }
    
            if(!this.production) {
                console.warn('DevMode Active')
    
                if(this.externalParameters.forceEnv != null) {
                    // se sono in modalità sviluppo carico eventualmente le variabili di environment forzate tramite query string
                    
                    let _cObj = JSON.parse(this.externalParameters.forceEnv)
    
                    for(let i in _cObj) {
                        this.environment[i] = _cObj[i]
                    }
                }
            }
    
            console.log('Environment loaded', this.environment)

            if(this.frontendVersion != null) {
                console.log('Frontend ver. ' + this.getVersionReport(this.frontendVersion))
            }
            
            if(this.apiVersion != null) {
                console.log('API ver. ' + this.apiVersion)
            }

            if(!ignoreWebSocket && this.environment.WebSocket != null) {
                let _socket = new Socket({ 
                    url: this.environment.WebSocket, 
                    options: {} 
                })
    
                _socket.on('version', (data) => { 
                    this.websocketVersionLocal = data.local;
                    this.websocketVersionGlobal = data.global;

                    if(this.websocketVersionLocal != null) {
                        console.log('Websocket Local ver. ' + this.getVersionReport(this.websocketVersionLocal))
                    }

                    if(this.websocketVersionGlobal != null) {
                        console.log('Websocket Global ver. ' + this.getVersionReport(this.websocketVersionGlobal))
                    }
                });
            }

        }
    }

    getDefaultCurrency() {
        return this.environment.AvailableContentCurrencies[0];
    }

    private _defaultApplicationsURLs = {
        Entourance: '/entourance',
        WebTour: '/webtour',
        Checkout: '/checkout',
        Forms: '/forms',
        ExperienceFinder: '/experience-finder',
        Totem: '/totem',
        CustomerCare: '/customer-care',
        Viewer: '/viewer',
    }

    getApplicationURL(application:'Entourance'|'WebTour'|'Checkout'|'Forms'|'ExperienceFinder'|'Totem'|'CustomerCare'|'Viewer') {
        if(this.environment[application + 'URL'] != null) {
            return this.environment[application + 'URL'];
        }
        else {
            return window.location.protocol + '//' + window.location.host + this._defaultApplicationsURLs[application]
        }
    }

    private _getApplicationVersion() {
        return new Promise<VersionData>((resolve) => {
            this.http.get('assets/release.json', {
                headers: { 'X-Interceptor-Silent': '*' }
            }).pipe(catchError(() => {
                return of(null as Object)
            })).subscribe((data:any) => {
                resolve(data)
            })
        })
    }

    private _watchInterval = 60000

    private async _watchApplicationVersion() {
        this.frontendVersion = await this._getApplicationVersion();

        setInterval(() => {
            this._getApplicationVersion().then((data) => {
                if(data != null && data.time != this.frontendVersion.time) {
                    this.frontendVersionChange.next(data)
                }
            })
        }, this._watchInterval)
    }

    private _watchApplicationStatus(env:any) {        
        setInterval(() => {
            this._loadApplicationStatus(env).then(() => {}, () => {})
        }, this._watchInterval)

        return this._loadApplicationStatus(env)
    }

    private _loadApplicationStatus(env:any) {
        return new Promise<void>((resolve) => {
            if(env.StatusURL == null) {
                this._handleApplicationStatus({})

                resolve()
            }
            else {
                this.http.get(env.StatusURL + '?_=' + Date.now(), {
                    headers: { 'X-Interceptor-Silent': '*' }
                }).pipe(catchError(() => {
                    return of({} as Object)
                })).subscribe((data) => {
                    
                    /*data = { 
                        maintenance: { 
                            from: new Date(Date.now() + 1250100).toISOString(), 
                            //to: new Date(Date.now() + 1910000).toISOString() 
                        } 
                    }*/

                    this._handleApplicationStatus(data)

                    resolve()
                })
            }
        })
    }

    private _handleApplicationStatus(data:any) {
        if(JSON.stringify(this.applicationStatus) != JSON.stringify(data)) { // NB: questo compare andrebbe fatto meglio
            this.applicationStatus = data;
            this.applicationStatusChange.next(this.applicationStatus)
        }
    }

    getVersionString(version?:VersionData) {
        if(version == null) version = this.frontendVersion;

        if(version != null) {
            let _versionString = version.major + '.' + version.minor + '.' + version.patch;
            if(version.prefix != null) _versionString = version.prefix + '-' + _versionString;
            if(version.suffix != null) _versionString = _versionString + '-' + version.suffix;

            return _versionString;
        }
    }

    getBuildTime(version?:VersionData) {
        if(version == null) version = this.frontendVersion;
        
        if(version != null) {
            return version.time;
        }
    }

    getBuildTimeString(version?:VersionData) {
        return new Date(this.getBuildTime(version)).toLocaleString(undefined, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    }

    getVersionReport(version?:VersionData) {
        if(version == null) version = this.frontendVersion;
        
        if(version != null) {
            return this.getVersionString(version) + ' - ' + this.getBuildTimeString(version);
        }
    }

    getVersionsTable(plain?:boolean) {
        let _versionIndex:{ [id:string]: string|VersionData } = {
            'Entourance API': this.apiVersion, 
            'Frontend': this.frontendVersion, 
            'Websocket Local': this.websocketVersionLocal, 
            'Websocket Global': this.websocketVersionGlobal
        }

        let _retVal = '';

        for(let i in _versionIndex) {
            let _version = _versionIndex[i];

            if(_version != null) {
                if(plain) {
                    if(_retVal != '') _retVal += '\r\n ';

                    _retVal += i + ': ';

                    if(typeof _version == 'string') {
                        _retVal += _version;
                    }
                    else {
                        _retVal += this.getVersionString(_version)

                        let _time = this.getBuildTimeString(_version)
                        if(_time != null) {
                            _retVal += ' - ' + _time;
                        }
                    }
                }
                else {
                    if(_retVal != '') {
                        _retVal += '<tr><td colspan="2"><hr class="my-1"/></td></tr>'
                    }
        
                    _retVal += '<tr><td>' + i + '</td>';
        
                    if(typeof _version == 'string') {
                        _retVal += '<td class="ps-2">' + _version + '</td></tr>'
                    }
                    else {
                        _retVal += '<td class="ps-2">' + this.getVersionString(_version) + '</td></tr>'
        
                        let _time = this.getBuildTimeString(_version)
                        if(_time != null) {
                            _retVal += '<tr><td colspan="2" class="text-muted small">' + _time + '</td></tr>'
                        }
                    }  
                }
            }
        }

        if(!plain) {
            _retVal = '<table class="PGVersionTable">' + _retVal + '</table>'; 
        }

        return _retVal;
    }

    getTimeUntilManteinance() {
        if(this.applicationStatus.maintenance != null) {
            if(this.applicationStatus.maintenance.from == null) return 0;
            else return(new Date(this.applicationStatus.maintenance.from).getTime() - Date.now())
        }
        else return Infinity;
    }

    isWebsocketEnabled() {
        return this.environment.WebSocket != null;
    }

    isConnectorActive(connector:ConnectorType) {
        return !!(this.environment.ConnectorsEnabled != null && this.environment.ConnectorsEnabled[connector]);
    }
}